import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
}

// Route to label mapping
const routeLabels: Record<string, string> = {
  '': 'Home',
  'services': 'Services',
  'gardening': 'Gardening',
  'cleaning': 'Cleaning',
  'taxidermy': 'Taxidermy',
  'contact': 'Contact',
  'gallery': 'Gallery',
  'about': 'About',
  'legal': 'Legal',
  'privacy': 'Privacy Policy',
  'terms': 'Terms of Service',
  'disclaimer': 'Disclaimer',
  'shipping': 'Shipping',
  'accessibility': 'Accessibility',
};

/**
 * Breadcrumb navigation with schema markup for SEO
 */
export function Breadcrumbs({ items }: BreadcrumbsProps) {
  const location = useLocation();

  // Generate breadcrumbs from URL if not provided
  const breadcrumbs: BreadcrumbItem[] = items || (() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const crumbs: BreadcrumbItem[] = [{ label: 'Home', href: '/' }];

    let path = '';
    segments.forEach((segment, index) => {
      path += `/${segment}`;
      const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      crumbs.push({
        label,
        href: index === segments.length - 1 ? undefined : path,
      });
    });

    return crumbs;
  })();

  // Don't show breadcrumbs on home page
  if (location.pathname === '/') {
    return null;
  }

  // Generate schema.org BreadcrumbList
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href
        ? `https://cloud9tasmania.com${item.href}`
        : `https://cloud9tasmania.com${location.pathname}`,
    })),
  };

  return (
    <>
      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      {/* Visual Breadcrumbs */}
      <nav
        aria-label="Breadcrumb"
        className="border-b border-border bg-muted/30 py-3"
      >
        <div className="container-custom">
          <ol className="flex flex-wrap items-center gap-1 text-sm">
            {breadcrumbs.map((item, index) => (
              <li key={index} className="flex items-center">
                {index > 0 && (
                  <ChevronRight
                    className="mx-1 h-4 w-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
                {item.href ? (
                  <Link
                    to={item.href}
                    className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                  >
                    {index === 0 && <Home className="h-3.5 w-3.5" />}
                    {item.label}
                  </Link>
                ) : (
                  <span className="font-medium text-foreground">
                    {item.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </>
  );
}
